import { z } from 'zod';
import { AssetTypeSchema } from './asset-api.dto';
import {
  PreferenceCardCaseTypeDtoSchema,
  UntrackedAssetTypeSchema,
} from './cases-api.dto';

// ─── List-level item schemas ──────────────────────────────────────────────────
// Simpler than TrackedAssetCatalogItemDtoSchema — Zod strips extra fields
// (availableCount, availableAssetLocations, manufacturer, productNumber) automatically.

const PreferenceCardListTrackedItemSchema = z.object({
  catalogItemId: z.string(),
  type: AssetTypeSchema,
  name: z.string(),
  quantity: z.number(),
  note: z.string().optional(),
});
export type PreferenceCardListTrackedItemDto = z.infer<typeof PreferenceCardListTrackedItemSchema>;

const PreferenceCardListTrackedAssetsSchema = z.object({
  caseCarts: z.array(PreferenceCardListTrackedItemSchema),
  trays: z.array(PreferenceCardListTrackedItemSchema),
  equipment: z.array(PreferenceCardListTrackedItemSchema),
  implants: z.array(PreferenceCardListTrackedItemSchema),
  stretchers: z.array(PreferenceCardListTrackedItemSchema),
});
export type PreferenceCardListTrackedAssetsDto = z.infer<typeof PreferenceCardListTrackedAssetsSchema>;

const PreferenceCardListUntrackedItemSchema = z.object({
  untrackedAssetCatalogId: z.string(),
  type: UntrackedAssetTypeSchema,
  name: z.string(),
  quantity: z.number(),
  note: z.string().optional(),
});
export type PreferenceCardListUntrackedItemDto = z.infer<typeof PreferenceCardListUntrackedItemSchema>;

const PreferenceCardListUntrackedAssetsSchema = z.object({
  supplies: z.array(PreferenceCardListUntrackedItemSchema),
  singlePackedInstruments: z.array(PreferenceCardListUntrackedItemSchema).optional(),
  instruments: z.array(PreferenceCardListUntrackedItemSchema).optional(),
});
export type PreferenceCardListUntrackedAssetsDto = z.infer<typeof PreferenceCardListUntrackedAssetsSchema>;

// ─── List item DTO ────────────────────────────────────────────────────────────

export const PreferenceCardListSurgeonDtoSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  npiNumber: z.string().optional(),
});
export type PreferenceCardListSurgeonDto = z.infer<typeof PreferenceCardListSurgeonDtoSchema>;

export const PreferenceCardListItemDtoSchema = z.object({
  preferenceCardId: z.string(),
  hospitalPreferenceCardId: z.string(),
  surgeon: PreferenceCardListSurgeonDtoSchema,
  caseType: PreferenceCardCaseTypeDtoSchema,
  version: z.number(),
  isActive: z.boolean(),
  trackedAssets: PreferenceCardListTrackedAssetsSchema,
  untrackedAssets: PreferenceCardListUntrackedAssetsSchema,
});
export type PreferenceCardListItemDto = z.infer<typeof PreferenceCardListItemDtoSchema>;

export const PaginatedPreferenceCardsResponseDtoSchema = z.object({
  data: z.array(PreferenceCardListItemDtoSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});
export type PaginatedPreferenceCardsResponseDto = z.infer<
  typeof PaginatedPreferenceCardsResponseDtoSchema
>;

export interface PreferenceCardsFilterParams {
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  isActive?: boolean;
}

export interface CreatePreferenceCardItemDto {
  catalogItemId: string;
  quantity: number;
  note?: string | null;
}

export interface CreatePreferenceCardDto {
  surgeonId: string;
  caseTypeId: string;
  hospitalPreferenceCardId: string;
  trackedItems: CreatePreferenceCardItemDto[];
  untrackedItems: CreatePreferenceCardItemDto[];
}

export interface UpdatePreferenceCardDto {
  trackedItems: CreatePreferenceCardItemDto[];
  untrackedItems: CreatePreferenceCardItemDto[];
}
